import { Injectable } from '@angular/core';
import { map, Observable, Observer, of, tap, catchError, BehaviorSubject } from 'rxjs';
import { Configuration } from 'src/app/app.constants';
import { DashboardWidgetViewModel } from 'src/app/modules/settings/viewModels/dashboardWidgetViewModel';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { EmployeeSettingViewModel } from '../viewModels/employeeSettingViewModel';
import { EmployeeDetailsViewModel } from '../viewModels/employeeDetailsViewModel';
import { AuthenticationService } from './authentication.service';
import { CachingService } from './caching.service';
import { LoginEventsEmitter } from '../events/login.events';
import { EmployeeSettingTypes } from '../../settings/enums/employeeSettingTypes';
import { ValidatedViewModel } from '../viewModels/validatedViewModel';
import { AccountSelectItemViewModel } from '../viewModels/accountSelectItemViewModel';
import { Employee } from '../models/employee';
import { ObjectTypes } from '../enums/objectTypes';
import { FilterViewModel } from '../models/filter/filterViewModel';
import { AccountSettingTypes } from '../enums/accountSettingTypes';

@Injectable({
  providedIn: 'root'
})
export class EmployeeSettingsService {

  constructor(
    private http: HttpClient,
    private configuration: Configuration,
    private cachingService: CachingService,
    private authenticationService: AuthenticationService,
    private loginEventEmitters: LoginEventsEmitter
  ) {

  }

  /**
   * Getter that dynamically builds the actionUrl each time.
   * This ensures the URL updates when the backend API changes (e.g., account switching).
   */
  private get actionUrl(): string {
    return this.configuration.buildEndpoint(`EmployeeSettings/`);
  }

  private readonly _settingsUpdated = new BehaviorSubject<EmployeeSettingTypes[]>([]);
  public readonly settingsUpdated$ = this._settingsUpdated.asObservable();

  private settingTypes: Map<EmployeeSettingTypes, EmployeeSettingViewModel> = new Map<EmployeeSettingTypes, EmployeeSettingViewModel>();

  /**
   * Initializes the employee settings cache with provided settings and notifies subscribers
   * @param employeeSettings - Array of employee settings to cache
   */
  public initializeSettings(employeeSettings: EmployeeSettingViewModel[]) {
    if (!employeeSettings || employeeSettings.length === 0) return;

    const updatedTypes: EmployeeSettingTypes[] = [];
    this.settingTypes.clear();

    employeeSettings.forEach(setting => {
      if (setting && setting.settingType) {
        this.settingTypes.set(setting.settingType, setting);
        updatedTypes.push(setting.settingType);
      }
    });

    if (updatedTypes.length > 0) {
      this._settingsUpdated.next(updatedTypes);
    }
  }

  /**
   * Gets the logged-in employee for route guards, handling authentication state changes
   * @returns Observable of Employee
   */
  public getLoggedInEmployeeForGuards() {
    const account = this.cachingService.GetAccountInfo();
    let employee = account ? this.cachingService.GetEmployee(account.id) : null;
    if (!account) {
      return new Observable<Employee>((observer: Observer<Employee>) => {
        this.loginEventEmitters.currentAccountChanged$.subscribe((account) => {
          console.log('account changed')
          employee = this.cachingService.GetEmployee(account.id);
          if (!employee) {
            this.loginEventEmitters.currentEmployeeChanged$.subscribe((res) => {
              console.log('employee changed');
              observer.next(res);
            });
          } else {
            console.log('employee found');
            return observer.next(employee);
          }
        });
      });
    } else if (!employee) {
      return new Observable<Employee>((observer: Observer<Employee>) => {
        this.loginEventEmitters.currentEmployeeChanged$.subscribe((res) => {
          observer.next(res);
        });
      });
    } else {
      return of(employee);
    }
  }

  /**
   * Gets the list of applicable accounts for the current user
   * @returns Observable of AccountSelectItemViewModel array
   */
  public getApplicableAccounts(): Observable<AccountSelectItemViewModel[]> {
    return this.http.get<AccountSelectItemViewModel[]>(this.actionUrl + `ApplicableAccounts`);
  }

  public getSignalRToken(): Observable<{token:string}> {
    return this.http.get<{token:string}>(this.actionUrl + `GetSignalRToken`);
  }

  /**
   * Gets the current employee token
   * @returns Observable of string token
   */
  public getCurrentEmployee(): Observable<string> {
    return this.http.get(this.actionUrl + `CurrentEmployee`).pipe(
      map((res: any) => {
        return res.token;
      })
    );
  }

  /**
   * Gets the dashboard widget layout for a specific setting type
   * @param settingType - The employee setting type to get layout for
   * @returns Observable of DashboardWidgetViewModel array
   */
  public getEmployeeDashboardWidgetLayout(settingType: EmployeeSettingTypes): Observable<DashboardWidgetViewModel[]> {
    return this.http.get<DashboardWidgetViewModel[]>(`${this.actionUrl}GetEmployeeDashboardWidgetLayout/${settingType}`);
  }

  /**
   * Updates the dashboard widget layout for a specific setting type
   * @param widgets - Array of dashboard widgets to update
   * @param settingType - The employee setting type to update layout for
   * @returns Observable of void
   */
  updateEmployeeDashboardWidgets(widgets: DashboardWidgetViewModel[], settingType: EmployeeSettingTypes): Observable<void> {
    return this.http.post<void>(`${this.actionUrl}UpdateEmployeeDashboardWidgetLayout/${settingType}`, widgets);
  }

  /**
   * Gets the employee receive notification setting
   * @returns Observable of EmployeeSettingViewModel for notification settings
   */
  public getEmployeeReceiveNotificationSetting(): Observable<EmployeeSettingViewModel> {
    return this.http.get<EmployeeSettingViewModel>(this.actionUrl + 'GetEmployeeReceiveNotificationsSetting');
  }

  /**
   * Updates the employee receive notification setting
   * @param setting - The notification setting to update
   * @returns Observable of void
   */
  updateEmployeeReceiveNotificationSetting(setting: EmployeeSettingViewModel): Observable<void> {
    return this.http.post<void>(this.actionUrl + 'UpdateEmployeeReceiveNotificationsSetting', setting);
  }

  /**
   * Gets an employee setting by type, first checking cache then fetching from server if needed
   * @param settingType - The type of employee setting to retrieve
   * @returns Observable of EmployeeSettingViewModel
   */
  public getEmployeeSetting(settingType: EmployeeSettingTypes): Observable<EmployeeSettingViewModel> {
    if (this.settingTypes.has(settingType)) {
      const setting = this.settingTypes.get(settingType);
      return of(setting);
    } else {
      return this.getEmployeeSettingHttp(settingType);
    }
  }

  /**
   * Fetches an employee setting from the server via HTTP and caches the result
   * @param settingType - The type of employee setting to fetch
   * @returns Observable of EmployeeSettingViewModel
   */
  public getEmployeeSettingHttp(settingType: EmployeeSettingTypes): Observable<EmployeeSettingViewModel> {
    return this.http.get<EmployeeSettingViewModel>(this.actionUrl + 'GetEmployeeSetting/' + settingType.toString())
    .pipe(map((result) => {
      this.settingTypes.set(settingType, result);
      return result;
    }));
  }

  /**
   * Updates an employee setting on the server and updates the local cache
   * @param settingType - The type of employee setting to update
   * @param setting - The setting data to update
   * @returns Observable of void
   */
  public updateEmployeeSetting(settingType: EmployeeSettingTypes, setting: EmployeeSettingViewModel): Observable<void> {
    return this.http.post<void>(this.actionUrl + 'UpdateEmployeeSetting/' + settingType.toString(), setting).pipe(
      tap(() => {
        const settingToCache = { ...setting };
        if (settingToCache.settingType !== settingType) {
          settingToCache.settingType = settingType;
        }
        this.settingTypes.set(settingType, settingToCache);
        this._settingsUpdated.next([settingType]);
      })
    );
  }

  /**
   * Updates an employee setting in the user area for a specific employee
   * @param employeeId - The ID of the employee to update settings for
   * @param settingType - The numeric setting type
   * @param setting - The setting data to update
   * @returns Observable of void
   */
  updateEmployeeSettingInUserArea(employeeId: number, settingType: number, setting: EmployeeSettingViewModel): Observable<void> {
    return this.http.post<void>(this.actionUrl + 'UpdateEmployeeSettingInUsersArea/' + employeeId + '/' + settingType, setting);
  }

  /**
   * Gets the details of the current employee
   * @returns Observable of EmployeeDetailsViewModel
   */
  getEmployeeDetails(): Observable<EmployeeDetailsViewModel> {
    return this.http.get<EmployeeDetailsViewModel>(this.actionUrl + 'GetEmployeeDetails');
  }

  updateEmployeeDetails(employeeDetailsViewModel: EmployeeDetailsViewModel): Observable<string> {
    return this.http.post<string>(this.actionUrl + 'UpdateEmployeeDetails', employeeDetailsViewModel);
  }

  setEmployeeLiveEvent(eventId: number): Observable<string> {
    return this.http.post<string>(this.actionUrl + 'SetEmployeeLiveEvent/' + eventId, '');
  }

  getToken(accountId: number, broadcastChange: boolean = true): Observable<string> {
    const options = this.authenticationService.getHttpOptions();
    return this.http.post(`${this.actionUrl}Details`, null, options).pipe(
      map((res: string) => {
        this.cachingService.AddEmployeeData(res, accountId);

        if (broadcastChange) this.loginEventEmitters.broadcastCurrentEmployeeChanged(this.cachingService.GetEmployee(accountId));

        return res;
      })
    );
  }

  getEmployeesDetails(): Observable<EmployeeDetailsViewModel[]> {
    return this.http.get<EmployeeDetailsViewModel[]>(this.actionUrl + `GetEmployeesDetails`);
  }

  getExternalUsersDetails(): Observable<EmployeeDetailsViewModel[]> {
    return this.http.get<EmployeeDetailsViewModel[]>(this.actionUrl + `GetEmployeesDetails?getOnlyExternalUsers=true`);
  }

  updateEmployeeFullDetails(employeeDetailsViewModel: EmployeeDetailsViewModel): Observable<void> {
    return this.http.post<void>(this.actionUrl + 'UpdateEmployeeFullDetails', employeeDetailsViewModel);
  }

  updateEmployeeZone(employeeDetailsViewModel: EmployeeDetailsViewModel): Observable<void> {
    return this.http.post<void>(this.actionUrl + 'UpdateEmployeeZone', employeeDetailsViewModel);
  }

  updateEmployeePermissions(employeeDetailsViewModel: EmployeeDetailsViewModel): Observable<void> {
    return this.http.post<void>(this.actionUrl + 'Permissions', employeeDetailsViewModel);
  }

  deleteEmployees(employeeIdsToDelete: number[]): Observable<void> {
    return this.http.post<void>(`${this.actionUrl}DeleteEmployees`, employeeIdsToDelete);
  }

  addEmployee(employeeDetailsViewModel: EmployeeDetailsViewModel): Observable<ValidatedViewModel> {
    return this.http.post<ValidatedViewModel>(this.actionUrl + 'AddEmployee', employeeDetailsViewModel);
  }

  completeOnboarding(): Observable<any> {
    return this.http.post<number>(this.actionUrl + 'CompleteOnboarding', null);
  }

  getPEAPSubscribersList(): Observable<EmployeeDetailsViewModel[]> {
    return this.http.get<EmployeeDetailsViewModel[]>(this.actionUrl + `GetPEAPSubscribersList`);
  }

  getConfidentialIDs(objectType: ObjectTypes): Observable<FilterViewModel[]> {
    return this.http.post<FilterViewModel[]>(`${this.actionUrl}PrivacyItems/${objectType}`, {});
  }

  getEmployeeValueBySettingType(accountSettingType: AccountSettingTypes): Observable<number> {
    return this.http.get<number>(this.actionUrl + `GetEmployeeValueBySettingType/${accountSettingType}`);
  }
}
